import { videos } from "@/mocks/videos";
import { categories, durationRanges } from "@/mocks/categories";
import type { Category, SortKey, Video, VideoFilters } from "@/types";

const allVideos = videos as unknown as Video[];
const allCategories = categories as unknown as Category[];

export function getAllVideos(): Video[] {
  return allVideos;
}

export function getCategories(): Category[] {
  return allCategories;
}

export function getVideoById(id: string | undefined): Video | undefined {
  if (!id) return undefined;
  return allVideos.find((v) => v.id === id);
}

export function getCategoryById(id: string | undefined): Category | undefined {
  if (!id) return undefined;
  return allCategories.find((c) => c.id === id);
}

export function getVideosByCategory(categoryId: string): Video[] {
  return allVideos.filter((v) => v.categoryId === categoryId);
}

export function getFeaturedVideos(ids: string[]): Video[] {
  return ids
    .map((id) => allVideos.find((v) => v.id === id))
    .filter((v): v is Video => Boolean(v));
}

export function getLatestVideos(limit?: number): Video[] {
  const sorted = [...allVideos].sort(
    (a, b) => new Date(b.uploadTime).getTime() - new Date(a.uploadTime).getTime(),
  );
  return typeof limit === "number" ? sorted.slice(0, limit) : sorted;
}

export function getHotVideos(limit?: number): Video[] {
  const sorted = [...allVideos].sort((a, b) => b.plays + b.favorites * 2 - (a.plays + a.favorites * 2));
  return typeof limit === "number" ? sorted.slice(0, limit) : sorted;
}

export function getRelatedVideos(video: Video, limit = 6): Video[] {
  const scored = allVideos
    .filter((v) => v.id !== video.id)
    .map((v) => {
      let score = 0;
      if (v.categoryId === video.categoryId) score += 3;
      score += v.tags.filter((t) => video.tags.includes(t)).length * 2;
      if (v.genre === video.genre) score += 1;
      return { v, score };
    })
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score || b.v.plays - a.v.plays);
  return scored.slice(0, limit).map((item) => item.v);
}

export function searchVideos(keyword: string): Video[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return allVideos.filter((v) => {
    const haystack = [v.title, v.subtitle, v.genre, v.region, v.categoryName, ...v.tags]
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
}

export function applyFilters(list: Video[], filters: VideoFilters): Video[] {
  return list.filter((v) => {
    if (filters.region !== "全部" && v.region !== filters.region) return false;
    if (filters.genre !== "全部" && v.genre !== filters.genre) return false;
    if (filters.year !== "全部") {
      if (filters.year === "2023 及更早") {
        if (v.year > 2023) return false;
      } else if (String(v.year) !== filters.year) {
        return false;
      }
    }
    if (filters.duration !== "全部") {
      const range = durationRanges[filters.duration];
      if (range) {
        if (v.durationMinutes < range[0] || v.durationMinutes >= range[1]) return false;
      }
    }
    return true;
  });
}

export function sortVideos(list: Video[], sort: SortKey): Video[] {
  const copy = [...list];
  if (sort === "plays") return copy.sort((a, b) => b.plays - a.plays);
  if (sort === "favorites") return copy.sort((a, b) => b.favorites - a.favorites);
  return copy.sort((a, b) => new Date(b.uploadTime).getTime() - new Date(a.uploadTime).getTime());
}

export function countActiveFilters(filters: VideoFilters): number {
  return Object.values(filters).filter((v) => v !== "全部").length;
}